import { memo } from "react";
import { buildLocalSrcset, localFallbackPath, nativeDimensions, SIZES } from "@/utils/images";

interface OptimizedImageProps {
  /** Sequence ID key — maps to public/images/source/{seqId}.jpg + optimized variants */
  seqId: string;
  alt: string;
  title?: string;
  /** CSS layout pattern — picks the correct `sizes` hint */
  layout?: keyof typeof SIZES;
  /** Whether this image is above the fold (hero, first screen) */
  aboveFold?: boolean;
  /** Explicit width override (defaults to manifest value) */
  width?: number;
  /** Explicit height override (defaults to manifest value) */
  height?: number;
  /** Object-fit value */
  fit?: "cover" | "contain" | "fill";
  /** Object-position value */
  position?: string;
  /** Extra classes for the wrapper div */
  wrapperClassName?: string;
  /** Extra classes for the img itself */
  imgClassName?: string;
  /** Optional callback when image loads */
  onLoad?: () => void;
  /** Optional callback when image errors */
  onError?: () => void;
}

const OptimizedImage = memo(function OptimizedImage({
  seqId,
  alt,
  title,
  layout = "articleBody",
  aboveFold = false,
  width,
  height,
  fit = "cover",
  position = "object-top",
  wrapperClassName = "",
  imgClassName = "",
  onLoad,
  onError,
}: OptimizedImageProps) {
  const dims = nativeDimensions(seqId);

  return (
    <div className={`w-full h-full ${wrapperClassName}`}>
      <picture>
        <source
          type="image/avif"
          srcSet={buildLocalSrcset(seqId, "avif")}
          sizes={SIZES[layout]}
        />
        <source
          type="image/webp"
          srcSet={buildLocalSrcset(seqId, "webp")}
          sizes={SIZES[layout]}
        />
        <img
          src={localFallbackPath(seqId)}
          alt={alt}
          title={title}
          loading={aboveFold ? "eager" : "lazy"}
          fetchPriority={aboveFold ? "high" : "auto"}
          decoding={aboveFold ? "sync" : "async"}
          width={width ?? dims?.width}
          height={height ?? dims?.height}
          className={`w-full h-full object-${fit} ${position} ${imgClassName}`}
          onLoad={onLoad}
          onError={onError}
        />
      </picture>
    </div>
  );
});

export default OptimizedImage;
